/**
 * V2MOM List
 *
 * Every V2MOM in the workspace: whose it is, what period, how far along, where
 * it is in the process, and which V2MOM it aligns to. Above the grid, what the
 * process puts in front of this person: the V2MOMs they were asked to write
 * (with a Start button), and the ones waiting for their review. Gated by the
 * `v2mom` workspace module + V2MOM_NAV.
 *
 * Route: /prioritization/strategy/v2mom
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlignVerticalJustifyStart, Plus, Loader2, AlertCircle, Trash2, ArrowRight, Inbox } from 'lucide-react';
import { PageHeader } from '../../../../components/prioritization/PageHeader';
import { EmptyState, Callout, Panel } from '@/components/ds';
import { btnPrimary, btnSecondary, status as statusReadout, LABEL_MONO, type Tone } from '@/components/mono/primitives';
import { useV2momStore } from '../../../../stores/prioritization/v2momStore';
import { useTenantStore } from '../../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import { useOrgUnits, useEnterpriseIdentityStore } from '../../../../stores/prioritization/enterpriseIdentityStore';
import { useWorkspaceMembers } from '../../../../hooks/useWorkspaceMembers';
import { formatDate } from '../../../../i18n/formatters';
import {
  dueState,
  openAsksFor,
  v2momsWaitingOn,
  v2momStanding,
  V2MOM_SECTIONS,
  V2MOM_STATUS_LABEL,
  v2momSectionFill,
  type V2mom,
  type V2momAsk,
  type V2momEntry,
  type V2momStatus,
} from '../../../../lib/v2mom/v2momModel';

const STATUS_TONE: Record<V2momStatus, Tone> = {
  draft: 'neutral',
  in_review: 'mustard',
  published: 'green',
  locked: 'slate',
};

function deleteConfirmMessage(v2mom: V2mom, childCount: number): string {
  let msg = `Delete "${v2mom.title}"?\n\nIts values, methods, obstacles and measures go with it, and so do the asks sent from it.`;
  if (childCount > 0) {
    msg += `\n\n${childCount} V2MOM${childCount === 1 ? '' : 's'} align${childCount === 1 ? 's' : ''} to this one. They stay, but lose their parent link.`;
  }
  msg += '\n\nThis cannot be undone.';
  return msg;
}

// ── Asked of you ─────────────────────────────────────────────────

function AskRow({ ask, parentTitle, askerName }: { ask: V2momAsk; parentTitle: string; askerName: string }) {
  const navigate = useNavigate();
  const startFromAsk = useV2momStore((s) => s.startFromAsk);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const due = dueState(ask.dueOn);

  async function start() {
    setBusy(true);
    setError(null);
    const { v2mom, error: err } = await startFromAsk(ask.id);
    setBusy(false);
    if (v2mom) navigate(`/prioritization/strategy/v2mom/${v2mom.id}`);
    else setError(err ?? 'Could not start the V2MOM.');
  }

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[var(--text-pri)]">
          Write your V2MOM against <span className="font-semibold">“{parentTitle}”</span>
        </p>
        <p className="mt-0.5 text-xs text-[var(--text-ter)]">
          {askerName} asked
          {ask.note ? <> · “{ask.note}”</> : null}
        </p>
        {error && <p className="mt-1 text-xs text-[var(--coral)]">{error}</p>}
      </div>
      <div className="flex items-center gap-2">
        {due && <span {...statusReadout(due.overdue ? 'coral' : 'neutral')}>{due.label}</span>}
        {ask.v2momId ? (
          <Link to={`/prioritization/strategy/v2mom/${ask.v2momId}`} {...btnPrimary('h-8 px-2.5')}>
            Continue <ArrowRight className="h-3 w-3" />
          </Link>
        ) : (
          <button {...btnPrimary('h-8 px-2.5')} onClick={() => void start()} disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Start writing
          </button>
        )}
      </div>
    </li>
  );
}

// ── Card ────────────────────────────────────────────────────────

function V2momCard({
  v2mom,
  entries,
  parentTitle,
  orgUnitName,
  ownerName,
  childCount,
  mayDelete,
  onDelete,
}: {
  v2mom: V2mom;
  entries: V2momEntry[];
  parentTitle: string | null;
  orgUnitName: string | null;
  ownerName: string | null;
  childCount: number;
  mayDelete: boolean;
  onDelete: (id: string) => Promise<{ success: boolean; error?: string }>;
}) {
  const [deleting, setDeleting] = useState(false);
  const fill = v2momSectionFill(v2mom, entries);
  const filled = Object.values(fill).filter(Boolean).length;
  const href = `/prioritization/strategy/v2mom/${v2mom.id}`;
  const due = v2mom.status === 'draft' ? dueState(v2mom.dueOn) : null;

  async function handleDelete(e: React.MouseEvent) {
    e.preventDefault();
    if (!window.confirm(deleteConfirmMessage(v2mom, childCount))) return;
    setDeleting(true);
    const res = await onDelete(v2mom.id);
    setDeleting(false);
    if (!res.success && res.error) window.alert(res.error);
  }

  const metaLine = [ownerName, v2mom.periodLabel, orgUnitName].filter(Boolean).join(' · ');

  return (
    <div
      className="group relative flex flex-col gap-3 border bg-[var(--bg-raised)] p-5 transition-colors hover:bg-[var(--hover-tint)]"
      style={{ borderColor: 'var(--rule)' }}
    >
      <div className="flex items-start justify-between gap-2">
        <Link to={href} className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <AlignVerticalJustifyStart className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
            <span className="truncate font-heading text-sm font-semibold text-[var(--text-pri)]">{v2mom.title}</span>
          </div>
          {metaLine && <p className="mt-1 text-xs text-[var(--text-ter)]">{metaLine}</p>}
        </Link>
        <span className="flex flex-col items-end gap-1">
          <span {...statusReadout(STATUS_TONE[v2mom.status])}>{V2MOM_STATUS_LABEL[v2mom.status]}</span>
          {due && <span {...statusReadout(due.overdue ? 'coral' : 'neutral')}>{due.label}</span>}
          {v2mom.reopenRequestedAt && <span {...statusReadout('mustard')}>Reopen asked</span>}
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        {V2MOM_SECTIONS.map((s) => (
          <div
            key={s.key}
            title={s.label}
            className={`h-1.5 flex-1 ${fill[s.key] ? 'bg-[var(--slate)]' : 'bg-[var(--hover-tint)]'}`}
          />
        ))}
      </div>
      <p className={LABEL_MONO}>{filled} of {V2MOM_SECTIONS.length} sections filled</p>

      <p className="text-xs text-[var(--text-ter)]">
        {parentTitle ? (
          <>Aligns to <span className="text-[var(--text-sec)]">{parentTitle}</span></>
        ) : (
          'Top of its chain'
        )}
        {childCount > 0 && <> · {childCount} align{childCount === 1 ? 's' : ''} to it</>}
      </p>

      <div className="mt-auto flex items-center justify-between border-t pt-3" style={{ borderColor: 'var(--rule)' }}>
        <span className="text-xs text-[var(--text-ter)]">{formatDate(v2mom.updatedAt)}</span>
        <div className="flex items-center gap-2">
          {mayDelete && (
            <button {...btnSecondary('h-8 px-2.5 opacity-0 group-hover:opacity-100')} onClick={handleDelete} disabled={deleting} title="Delete V2MOM">
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
          <Link to={href} {...btnSecondary('h-8 px-2.5')}>
            Open <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────

export function V2momList() {
  const navigate = useNavigate();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const isAdmin = useUserStore((s) => s.can('admin.settings'));

  const v2moms = useV2momStore((s) => s.v2moms);
  const entriesByDoc = useV2momStore((s) => s.entriesByDoc);
  const asks = useV2momStore((s) => s.asks);
  const isLoading = useV2momStore((s) => s.isLoading);
  const syncError = useV2momStore((s) => s.syncError);
  const fetchV2moms = useV2momStore((s) => s.fetchV2moms);
  const createV2mom = useV2momStore((s) => s.createV2mom);
  const deleteV2mom = useV2momStore((s) => s.deleteV2mom);

  const orgUnits = useOrgUnits();
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const { nameOf } = useWorkspaceMembers(workspaceId);

  useEffect(() => {
    if (!workspaceId) return;
    void fetchV2moms();
    void fetchOrgUnits({ workspaceId });
  }, [workspaceId, fetchV2moms, fetchOrgUnits]);

  const titleById = useMemo(() => new Map(v2moms.map((d) => [d.id, d.title])), [v2moms]);
  const orgUnitNameById = useMemo(() => new Map(orgUnits.map((u) => [u.id, u.displayName || u.name])), [orgUnits]);
  const childCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of v2moms) if (d.parentId) counts.set(d.parentId, (counts.get(d.parentId) ?? 0) + 1);
    return counts;
  }, [v2moms]);

  const myAsks = useMemo(() => openAsksFor(asks, userId || null), [asks, userId]);
  const waitingOnMe = useMemo(() => v2momsWaitingOn(v2moms, userId || null, isAdmin), [v2moms, userId, isAdmin]);

  async function handleCreate() {
    const { v2mom, error } = await createV2mom({ title: 'Untitled V2MOM' });
    if (v2mom) navigate(`/prioritization/strategy/v2mom/${v2mom.id}`);
    else if (error) window.alert(`Could not create the V2MOM: ${error}`);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<AlignVerticalJustifyStart className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="V2MOM"
        subtitle="Vision, Values, Methods, Obstacles, Measures — one page per leader, each written against the one above it and accepted by its owner."
        actions={
          v2moms.length > 0 ? (
            <button {...btnPrimary()} onClick={handleCreate}>
              <Plus className="h-4 w-4" />
              New V2MOM
            </button>
          ) : null
        }
      />

      {syncError && (
        <Callout tone="coral" title="Could not load V2MOMs">
          <span className="inline-flex items-center gap-2"><AlertCircle className="h-4 w-4" />{syncError}</span>
        </Callout>
      )}

      {(myAsks.length > 0 || waitingOnMe.length > 0) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {myAsks.length > 0 && (
            <Panel label="Asked of you" title="V2MOMs to write" count={myAsks.length}>
              <ul className="divide-y" style={{ borderColor: 'var(--rule)' }}>
                {myAsks.map((a) => (
                  <AskRow key={a.id} ask={a} parentTitle={titleById.get(a.parentV2momId) ?? 'a V2MOM'} askerName={nameOf(a.askedBy)} />
                ))}
              </ul>
            </Panel>
          )}
          {waitingOnMe.length > 0 && (
            <Panel label="Waiting on you" title="To review" count={waitingOnMe.length}>
              <ul className="divide-y" style={{ borderColor: 'var(--rule)' }}>
                {waitingOnMe.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <Link to={`/prioritization/strategy/v2mom/${d.id}`} className="text-sm font-medium text-[var(--text-pri)] hover:underline">
                        {d.title}
                      </Link>
                      <p className="mt-0.5 text-xs text-[var(--text-ter)]">
                        {d.status === 'in_review'
                          ? `${nameOf(d.submittedBy ?? d.ownerId)} sent it for your review${d.submittedAt ? ` on ${formatDate(d.submittedAt)}` : ''}`
                          : `${nameOf(d.reopenRequestedBy)} asked to reopen it${d.reopenNote ? `: “${d.reopenNote}”` : ''}`}
                      </p>
                    </div>
                    <Link to={`/prioritization/strategy/v2mom/${d.id}`} {...btnPrimary('h-8 px-2.5')}>
                      <Inbox className="h-3.5 w-3.5" /> {d.status === 'in_review' ? 'Review' : 'Respond'}
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      )}

      {isLoading && v2moms.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        </div>
      )}

      {!isLoading && v2moms.length === 0 && !syncError && (
        <EmptyState
          size="page"
          icon={AlignVerticalJustifyStart}
          title="No V2MOMs yet"
          description="A V2MOM is one page: what we want (Vision), what matters as we pursue it (Values), how we get it done (Methods), what is in the way (Obstacles), and how we know we got there (Measures). Start with the top of the organization; publish it, then ask the people below to write theirs against it."
          action={
            <button {...btnPrimary()} onClick={handleCreate}>
              <Plus className="h-4 w-4" />
              Create the first V2MOM
            </button>
          }
        />
      )}

      {v2moms.length > 0 && (
        <>
          <p className={LABEL_MONO}>{v2moms.length} V2MOM{v2moms.length === 1 ? '' : 's'}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {v2moms.map((d) => {
              const standing = v2momStanding(d, v2moms, userId || null, isAdmin);
              const mayDelete = d.status === 'locked' ? standing.isAdmin : standing.canManage;
              return (
                <V2momCard
                  key={d.id}
                  v2mom={d}
                  entries={entriesByDoc[d.id] ?? []}
                  parentTitle={d.parentId ? (titleById.get(d.parentId) ?? null) : null}
                  orgUnitName={d.orgUnitId ? (orgUnitNameById.get(d.orgUnitId) ?? null) : null}
                  ownerName={d.ownerId ? nameOf(d.ownerId) : null}
                  childCount={childCounts.get(d.id) ?? 0}
                  mayDelete={mayDelete}
                  onDelete={deleteV2mom}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default V2momList;
